/**
 * Talking to our own routes, and turning a Better Auth failure into a
 * MessageKey.
 *
 * Better Auth reports errors with a `code`. Where the code came from one of
 * our own hooks it already is a MessageKey; where it is one of the library's
 * own, it is mapped here. Anything unrecognised becomes `error.unexpected`
 * rather than being shown to a user, because a library's error strings are
 * in English and are not ours to put in front of a Bulgarian reader.
 */

import type { ApiResponse, ClientConfigResponse } from '../../shared/api.ts'
import { isMessageKey, type MessageKey } from '../../shared/message-keys.ts'

const BETTER_AUTH_CODE_TO_KEY: Record<string, MessageKey> = {
  INVALID_EMAIL_OR_PASSWORD: 'error.invalid_credentials',
  INVALID_EMAIL: 'error.invalid_email',
  INVALID_PASSWORD: 'error.invalid_credentials',
  USER_NOT_FOUND: 'error.invalid_credentials',
  EMAIL_NOT_VERIFIED: 'error.email_not_verified',
  USER_ALREADY_EXISTS: 'error.email_taken',
  USER_EMAIL_NOT_FOUND: 'error.invalid_credentials',
  PASSWORD_TOO_SHORT: 'error.password_too_short',
  PASSWORD_TOO_LONG: 'error.password_too_long',
  INVALID_TOKEN: 'error.token_invalid',
  TOKEN_EXPIRED: 'error.token_expired',
  SESSION_EXPIRED: 'error.not_signed_in',
  FAILED_TO_CREATE_USER: 'error.unexpected',
}

export interface AuthFailure {
  code?: string | undefined
  status?: number | undefined
}

export function messageKeyFor(error: AuthFailure | null | undefined): MessageKey {
  if (!error) return 'error.unexpected'
  if (error.status === 429) return 'error.rate_limited'

  const code = error.code
  if (!code) return 'error.unexpected'
  // Our own hooks throw with a MessageKey as the code.
  if (isMessageKey(code)) return code
  return BETTER_AUTH_CODE_TO_KEY[code] ?? 'error.unexpected'
}

export async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: 'same-origin' })
  const body = (await response.json()) as ApiResponse<T>
  if (!body.ok) throw new Error(body.error.key)
  return body.data
}

export const fetchClientConfig = () => getJson<ClientConfigResponse>('/api/config')

// No `fetchSession`. Who the caller is comes from `authClient.useSession()`,
// which is Better Auth's own and is what every page already uses. See
// docs/decisions/004-no-session-endpoint.md.
